import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { Avatar, Box, BoxProps, styled } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { environment } from '../../config';
import usePortfolioQuery from '../../pages/Profile/components/usePortfolioQuery';
import Icons from '../../themes/icons';

type PhotoCardProps = BoxProps<'div'> & {
  error?: boolean;
  isDragActive?: boolean;
};

const baseUrl = (() => {
  const envUrl = environment === 'production' ? '' : `-${environment}`;
  return `https://assets${envUrl}.beautypass.app`;
})();

const PhotoCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'error' && prop !== 'isDragActive',
})<PhotoCardProps>(({ isDragActive, error }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '8px',
  backgroundColor: isDragActive ? '#e0f7fa' : '#F7F7F7',
  width: '100%',
  cursor: 'pointer',
  minHeight: '100%',
  border:
    error ? '2px solid #C65353' : isDragActive ? '2px dashed #00bcd4' : '2px dashed transparent',
  transition: 'all 0.2s ease-in-out',
}));

// Image validation constants
const IMAGE_REQUIREMENTS = {
  RECOMMENDED_WIDTH: 2000,
  RECOMMENDED_HEIGHT: 2000,
  MIN_SIZE: 100 * 1024, // 100KB in bytes
  MAX_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  ACCEPTED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png']
};

export type ValidationError = {
  type: 'size' | 'format';
  message: string;
  severity: 'error' | 'warning';
};

type PortfolioPhotoProps = {
  slotIndex: number;
  hasError: boolean;
  onValidationError: (error: ValidationError | null, slotIndex: number) => void;
};

export const PortfolioPhoto: React.FC<PortfolioPhotoProps> = ({
  slotIndex,
  hasError,
  onValidationError,
}) => {
  const { uploadMutation } = usePortfolioQuery();
  const [previewImg, setPreviewImg] = React.useState<string>();
  const [loading, setLoading] = React.useState<boolean>();

  const validateImage = (file: File): Promise<ValidationError | null> => {
    return new Promise((resolve) => {
      // Check file format
      if (!IMAGE_REQUIREMENTS.ACCEPTED_FORMATS.includes(file.type)) {
        resolve({
          type: 'format',
          message: 'Please upload a valid image format (JPEG, PNG, or JPG)',
          severity: 'error'
        });
        return;
      }

      // Check file size
      if (file.size < IMAGE_REQUIREMENTS.MIN_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too small. Minimum size is ${Math.round(IMAGE_REQUIREMENTS.MIN_SIZE / 1024)}KB`,
          severity: 'error'
        });
        return;
      }

      if (file.size > IMAGE_REQUIREMENTS.MAX_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too large. Maximum size is ${IMAGE_REQUIREMENTS.MAX_SIZE / (1024 * 1024)}MB`,
          severity: 'error'
        });
        return;
      }

      // Verify the file is a readable image
      const img = new Image();
      img.onload = () => {
        resolve(null); // No validation errors
      };

      img.onerror = () => {
        resolve({
          type: 'format',
          message: 'Unable to read image file. Please try a different image.',
          severity: 'error'
        });
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      onValidationError(null, slotIndex);
      setLoading(true);

      try {
        const validationResult = await validateImage(file);
        if (validationResult) {
          onValidationError(validationResult, slotIndex);
          setLoading(false);
          return;
        }

        const dataUrl = URL.createObjectURL(file);
        setPreviewImg(dataUrl);

        await uploadMutation.mutateAsync(file);

        setPreviewImg('');
        onValidationError(null, slotIndex);
      } catch (error) {
        console.error('Upload failed:', error);
        onValidationError(
          {
            type: 'size',
            message: 'Upload failed. Please try again or choose a different image.',
            severity: 'error',
          },
          slotIndex,
        );
      } finally {
        setLoading(false);
      }
    },
    [uploadMutation, onValidationError, slotIndex],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': [],
    },
    noClick: true,
    noKeyboard: true,
  });

  return (
    <Box sx={{ width: '100%' }}>
      <PhotoCard
        {...getRootProps()}
        isDragActive={isDragActive}
        error={hasError}
        onClick={!loading ? openFileDialog : undefined}
      >
        <input {...getInputProps()} disabled={loading} />

        {previewImg && (
          <Avatar
            src={previewImg}
            sx={{
              position: 'absolute',
              borderRadius: 'inherit',
              height: '100%',
              width: '100%',
              opacity: loading ? 0.5 : 1,
            }}
          />
        )}

        {!previewImg && !loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              p: 2,
              textAlign: 'center',
              color: hasError ? '#C65353' : undefined,
            }}
          >
            <Icons.AddIcon />
          </Box>
        )}

        {loading && <CircularProgress size={20} />}
      </PhotoCard>
    </Box>
  );
};
